import { useState, useEffect } from 'react'
import { marketingApi } from '../api/marketing'
import Modal from '../components/Modal'
import Boton from '../components/Boton'

const EMPTY = { nombre: '', descripcion: '', lista_id: '', salida_compra: true, pasos: [] }
const PASO_EMPTY = { espera_horas: 24, asunto: '', preheader: '', plantilla_id: '' }

/**
 * Automations — secuencias automáticas de emails (Fase 2 del módulo Marketing).
 * Caso fundante: perseguir leads de IG (cenas/eventos) con promos hasta que
 * compren o se den de baja.
 */
export default function MktAutomationsPage() {
  const [automations, setAutomations] = useState([])
  const [listas, setListas] = useState([])
  const [plantillas, setPlantillas] = useState([])
  const [modal, setModal] = useState(null) // 'new' | automation
  const [form, setForm] = useState(EMPTY)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')

  const cargar = () => marketingApi.automations().then(r => setAutomations(r.data))
  useEffect(() => {
    cargar()
    marketingApi.listas().then(r => setListas(r.data))
    marketingApi.plantillas().then(r => setPlantillas(r.data?.data ?? r.data))
  }, [])

  const openNew = () => { setForm({ ...EMPTY, pasos: [{ ...PASO_EMPTY, espera_horas: 0 }] }); setErr(''); setModal('new') }
  const openEdit = async (a) => {
    const r = await marketingApi.automation(a.id)
    const full = r.data
    setForm({
      nombre: full.nombre,
      descripcion: full.descripcion || '',
      lista_id: full.lista_id || '',
      salida_compra: full.salida_compra,
      pasos: (full.pasos || []).map(p => ({
        espera_horas: p.espera_horas, asunto: p.asunto,
        preheader: p.preheader || '', plantilla_id: p.plantilla_id || '',
      })),
    })
    setErr(''); setModal(full)
  }

  const setPaso = (i, k, v) => setForm(f => ({
    ...f,
    pasos: f.pasos.map((p, j) => (j === i ? { ...p, [k]: v } : p)),
  }))
  const agregarPaso = () => setForm(f => ({ ...f, pasos: [...f.pasos, { ...PASO_EMPTY }] }))
  const quitarPaso = (i) => setForm(f => ({ ...f, pasos: f.pasos.filter((_, j) => j !== i) }))
  const moverPaso = (i, dir) => setForm(f => {
    const pasos = [...f.pasos]
    const j = i + dir
    if (j < 0 || j >= pasos.length) return f
    ;[pasos[i], pasos[j]] = [pasos[j], pasos[i]]
    return { ...f, pasos }
  })

  const guardar = async (e) => {
    e.preventDefault(); setSaving(true); setErr('')
    const payload = {
      ...form,
      lista_id: form.lista_id || null,
      pasos: form.pasos.map(p => ({
        ...p,
        espera_horas: Number(p.espera_horas),
        plantilla_id: p.plantilla_id || null,
        preheader: p.preheader || null,
      })),
    }
    try {
      if (modal === 'new') await marketingApi.crearAutomation(payload)
      else await marketingApi.actualizarAutomation(modal.id, payload)
      setModal(null); cargar()
    } catch (error) { setErr(error.response?.data?.message || 'Error al guardar') }
    finally { setSaving(false) }
  }

  const toggleEstado = async (a) => {
    try {
      if (a.estado === 'activa') await marketingApi.pausarAutomation(a.id)
      else await marketingApi.activarAutomation(a.id)
      cargar()
    } catch (error) { window.alert(error.response?.data?.message || 'No se pudo cambiar el estado') }
  }

  const eliminar = async (a) => {
    if (!window.confirm(`¿Eliminar la automation "${a.nombre}"?`)) return
    try { await marketingApi.eliminarAutomation(a.id); cargar() }
    catch (error) { window.alert(error.response?.data?.message || 'No se pudo eliminar') }
  }

  return (
    <div>
      <div style={st.head}>
        <div>
          <h2 style={st.title}>Automations</h2>
          <p style={st.sub}>Secuencias automáticas: se inscriben solos al entrar a una lista y reciben los pasos hasta comprar o darse de baja.</p>
        </div>
        <Boton variante="primary" onClick={openNew}>+ Nueva</Boton>
      </div>

      <div style={st.cards}>
        {automations.map(a => (
          <div key={a.id} style={st.card}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={st.nombre}>
                {a.estado === 'activa' ? '🟢' : '⏸'} {a.nombre}
                <span style={a.estado === 'activa' ? st.badgeActiva : st.badgePausada}>
                  {a.estado === 'activa' ? 'ACTIVA' : 'PAUSADA'}
                </span>
              </p>
              <p style={st.desc}>
                {a.lista ? <>Dispara al entrar a <strong>{a.lista.nombre}</strong></> : 'Sin lista disparadora'}
                {' · '}{a.pasos_count} paso{a.pasos_count === 1 ? '' : 's'}
                {a.salida_compra ? ' · sale al comprar' : ''}
              </p>
              <p style={st.metricas}>
                <span title="Contactos recorriendo la secuencia">▶ {a.activas_count} activos</span>
                <span title="Salieron porque compraron" style={{ color: '#15803d' }}>✓ {a.compras_count} compraron</span>
                <span title="Terminaron la secuencia sin comprar">◼ {a.completadas_count} completaron</span>
                <span title="Se dieron de baja" style={{ color: '#b91c1c' }}>✕ {a.bajas_count} bajas</span>
              </p>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <Boton variante={a.estado === 'activa' ? 'secondary' : 'success'} sm onClick={() => toggleEstado(a)}>
                {a.estado === 'activa' ? '⏸ Pausar' : '▶ Activar'}
              </Boton>
              <Boton variante="secondary" sm onClick={() => openEdit(a)}>Editar</Boton>
              <Boton variante="destructive" sm onClick={() => eliminar(a)}>Eliminar</Boton>
            </div>
          </div>
        ))}
        {automations.length === 0 && (
          <p style={{ color: '#94a3b8' }}>
            Sin automations. Creá una secuencia (ej: lead de Instagram → bienvenida → promo a los 3 días → última chance a la semana).
          </p>
        )}
      </div>

      {modal && (
        <Modal title={modal === 'new' ? 'Nueva automation' : `Editar — ${modal.nombre}`} onClose={() => setModal(null)} maxWidth={680}>
          <form onSubmit={guardar} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={st.grid2}>
              <div>
                <label style={st.lbl}>Nombre *</label>
                <input style={st.inp} value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} required />
              </div>
              <div>
                <label style={st.lbl}>Lista disparadora *</label>
                <select style={st.inp} value={form.lista_id} onChange={e => setForm(f => ({ ...f, lista_id: e.target.value }))}>
                  <option value="">Elegir lista…</option>
                  {listas.map(l => <option key={l.id} value={l.id}>{l.nombre}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label style={st.lbl}>Descripción</label>
              <input style={st.inp} value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} placeholder="ej: persecución de leads de IG eventos" />
            </div>
            <label style={st.check}>
              <input type="checkbox" checked={form.salida_compra} onChange={e => setForm(f => ({ ...f, salida_compra: e.target.checked }))} />
              Sacar de la secuencia cuando compra (objetivo cumplido)
            </label>

            <div style={st.pasosBox}>
              <p style={st.pasosTitulo}>Pasos de la secuencia</p>
              {form.pasos.map((p, i) => (
                <div key={i} style={st.paso}>
                  <div style={st.pasoHead}>
                    <span style={st.pasoNum}>Paso {i + 1}</span>
                    <span style={st.pasoEspera}>
                      {i === 0 ? 'A las' : 'Después de'}
                      <input style={st.inpHoras} type="number" min="0" max="8760" value={p.espera_horas}
                        onChange={e => setPaso(i, 'espera_horas', e.target.value)} required />
                      horas {i === 0 ? 'de inscribirse' : 'del paso anterior'}
                    </span>
                    <span style={{ flex: 1 }} />
                    <Boton variante="ghost" sm type="button" onClick={() => moverPaso(i, -1)} disabled={i === 0}>↑</Boton>
                    <Boton variante="ghost" sm type="button" onClick={() => moverPaso(i, 1)} disabled={i === form.pasos.length - 1}>↓</Boton>
                    <Boton variante="destructive" sm type="button" onClick={() => quitarPaso(i)}>✕</Boton>
                  </div>
                  <div style={st.grid2}>
                    <div>
                      <label style={st.lbl}>Asunto *</label>
                      <input style={st.inp} value={p.asunto} onChange={e => setPaso(i, 'asunto', e.target.value)} required />
                    </div>
                    <div>
                      <label style={st.lbl}>Plantilla</label>
                      <select style={st.inp} value={p.plantilla_id} onChange={e => setPaso(i, 'plantilla_id', e.target.value)}>
                        <option value="">Sin plantilla (HTML propio)</option>
                        {plantillas.map(pl => <option key={pl.id} value={pl.id}>{pl.nombre}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label style={st.lbl}>Preheader</label>
                    <input style={st.inp} value={p.preheader} onChange={e => setPaso(i, 'preheader', e.target.value)} />
                  </div>
                </div>
              ))}
              <Boton variante="secondary" sm type="button" onClick={agregarPaso}>+ Agregar paso</Boton>
            </div>

            {err && <p style={{ color: '#dc2626', fontSize: '0.875rem', margin: 0 }}>{err}</p>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Boton variante="ghost" type="button" onClick={() => setModal(null)}>Cancelar</Boton>
              <Boton variante="primary" type="submit" disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</Boton>
            </div>
          </form>
        </Modal>
      )}
    </div>
  )
}

const st = {
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 16 },
  title: { margin: 0, fontSize: '1.4rem', color: '#0f172a' },
  sub: { margin: '4px 0 0', fontSize: '0.82rem', color: '#94a3b8' },
  cards: { display: 'flex', flexDirection: 'column', gap: 10 },
  card: { display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 16px' },
  nombre: { margin: 0, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 },
  badgeActiva: { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', background: '#dcfce7', color: '#15803d', padding: '2px 8px', borderRadius: 4 },
  badgePausada: { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', background: '#f1f5f9', color: '#64748b', padding: '2px 8px', borderRadius: 4 },
  desc: { margin: '2px 0 0', fontSize: '0.82rem', color: '#94a3b8' },
  metricas: { margin: '6px 0 0', fontSize: '0.82rem', color: '#64748b', display: 'flex', gap: 14 },
  lbl: { display: 'block', fontWeight: 600, fontSize: '0.82rem', color: '#374151', marginBottom: 4 },
  inp: { width: '100%', padding: '0.5rem 0.7rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box', background: '#fff' },
  inpHoras: { width: 70, padding: '0.25rem 0.4rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem', margin: '0 6px', fontVariantNumeric: 'tabular-nums' },
  check: { display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.875rem', color: '#374151' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  pasosBox: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 10 },
  pasosTitulo: { margin: 0, fontSize: '0.78rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase' },
  paso: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 },
  pasoHead: { display: 'flex', alignItems: 'center', gap: 6 },
  pasoNum: { fontWeight: 700, fontSize: '0.82rem', color: '#1e40af' },
  pasoEspera: { fontSize: '0.82rem', color: '#64748b', display: 'flex', alignItems: 'center' },
}
